import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

export interface DataQualityBadgeProps {
  score?: number | null;
  missingFields?: string[];
  onClick?: () => void;
  className?: string;
}

// Humanise a snake_case field name into something a recruiter recognises.
// Extend this map as the backend introduces new missing_fields tokens.
const FIELD_LABELS: Record<string, string> = {
  email: 'email',
  whatsapp_phone: 'WhatsApp number',
  cv: 'CV',
  skills: 'skills',
  current_job_title: 'current job title',
  current_location: 'current location',
  years_experience: 'years of experience',
  availability_date: 'availability date',
  expected_salary_range: 'expected salary',
  linkedin_url: 'LinkedIn profile',
  nationality: 'nationality',
  current_employer: 'current employer',
  education_level: 'education level',
  preferred_job_types: 'preferred job types',
  willing_to_relocate: 'relocation preference',
  id_number: 'ID number',
  first_name: 'first name',
  last_name: 'last name',
};

export function humaniseField(field: string): string {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  // Fallback: snake_case → Sentence case
  return field.replace(/_/g, ' ').toLowerCase();
}

function getScoreStyles(score: number): { bg: string; text: string; ring: string; label: string } {
  if (score >= 80) {
    return {
      bg: 'bg-emerald-500/15',
      text: 'text-emerald-400',
      ring: 'ring-emerald-500/30',
      label: 'Profile nearly complete',
    };
  }
  if (score >= 60) {
    return {
      bg: 'bg-amber-500/15',
      text: 'text-amber-400',
      ring: 'ring-amber-500/30',
      label: 'Profile incomplete',
    };
  }
  return {
    bg: 'bg-red-500/15',
    text: 'text-red-400',
    ring: 'ring-red-500/30',
    label: 'Profile missing critical data',
  };
}

/**
 * DataQualityBadge
 *
 * Small inline badge showing a candidate's data-quality score (0–100).
 *  - green  ≥80
 *  - amber  60–79
 *  - red    <60
 *
 * Hover reveals the list of missing fields. If the score is missing, renders a
 * neutral dash so the column doesn't collapse.
 */
export function DataQualityBadge({
  score,
  missingFields,
  onClick,
  className,
}: DataQualityBadgeProps) {
  if (score === undefined || score === null || Number.isNaN(score)) {
    return <span className="text-text-tertiary text-xs">—</span>;
  }

  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const styles = getScoreStyles(clamped);
  const hasMissing = Array.isArray(missingFields) && missingFields.length > 0;

  const badge = (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1',
        'transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-bg-primary',
        styles.bg,
        styles.text,
        styles.ring,
        onClick ? 'cursor-pointer hover:brightness-110' : 'cursor-default',
        className,
      )}
      aria-label={`${styles.label}: ${clamped} out of 100`}
    >
      <span>{clamped}</span>
    </button>
  );

  return (
    <TooltipPrimitive.Provider delayDuration={150}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{badge}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side="top"
            sideOffset={6}
            className={cn(
              'z-50 max-w-[260px] rounded-md border border-white/10 bg-bg-tertiary px-3 py-2',
              'text-xs text-text-primary shadow-lg',
              'data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
            )}
          >
            <div className="font-semibold">{styles.label} · {clamped}/100</div>
            {hasMissing ? (
              <div className="mt-1 text-text-secondary">
                Missing: {missingFields!.map(humaniseField).join(', ')}
              </div>
            ) : (
              <div className="mt-1 text-text-secondary">Profile complete</div>
            )}
            <TooltipPrimitive.Arrow className="fill-bg-tertiary" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

export default DataQualityBadge;
